import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, UserCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Searchable Referred-By selector.
 * Groups: Direct, Personal Contact, Team Members, Customers.
 *
 * Props:
 *  value:        { referrer_type: 'direct'|'personal_contact'|'team_member'|'customer', referrer_id: string }
 *  onChange:     ({ referrer_type, referrer_id }) => void
 *  teamMembers:  [{ id, name, role? }]
 *  customers:    [{ id, name, phone? }]
 *  excludeCustomerId: string (the current customer being edited — hidden from list)
 *  size:         'sm' | 'md'
 *  testId:       string
 */
const ReferrerSelect = ({ value, onChange, teamMembers = [], customers = [], excludeCustomerId = '', size = 'md', testId }) => {
  const [open, setOpen] = useState(false);
  const type = value?.referrer_type || 'direct';
  const id = value?.referrer_id || '';

  const label = (() => {
    if (type === 'direct') return '— Direct / None —';
    if (type === 'personal_contact') return 'Personal Contact';
    if (type === 'team_member') {
      const tm = teamMembers.find(t => t.id === id);
      return tm ? `${tm.name}${tm.role ? ` (${tm.role})` : ''}` : 'Team Member';
    }
    if (type === 'customer') {
      const c = customers.find(o => o.id === id);
      return c ? c.name : 'Customer';
    }
    return '— Direct / None —';
  })();

  const visibleCustomers = customers.filter(c => c.id !== excludeCustomerId);
  const heightCls = size === 'sm' ? 'h-8 text-xs px-2' : 'h-10 text-sm px-3';

  const selectAndClose = (next) => { onChange(next); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', heightCls)}
          data-testid={testId}>
          <span className="flex items-center gap-1.5 truncate">
            <UserCheck className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="truncate">{label}</span>
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start">
        <Command>
          <CommandInput placeholder="Search team members or customers…" className="h-9" />
          <CommandList>
            <CommandEmpty>No match found.</CommandEmpty>
            <CommandGroup heading="General">
              <CommandItem value="direct none" onSelect={() => selectAndClose({ referrer_type: 'direct', referrer_id: '' })}>
                <Check className={cn('w-4 h-4 mr-2', type === 'direct' ? 'opacity-100' : 'opacity-0')} />
                — Direct / None —
              </CommandItem>
              <CommandItem value="personal contact" onSelect={() => selectAndClose({ referrer_type: 'personal_contact', referrer_id: '' })}>
                <Check className={cn('w-4 h-4 mr-2', type === 'personal_contact' ? 'opacity-100' : 'opacity-0')} />
                Personal Contact
              </CommandItem>
            </CommandGroup>
            {teamMembers.length > 0 && (
              <CommandGroup heading="Team Members">
                {teamMembers.map(tm => {
                  const searchText = `team ${tm.name} ${tm.role || ''} ${tm.phone || ''}`.toLowerCase();
                  return (
                    <CommandItem key={tm.id} value={searchText} onSelect={() => selectAndClose({ referrer_type: 'team_member', referrer_id: tm.id })}>
                      <Check className={cn('w-4 h-4 mr-2', type === 'team_member' && id === tm.id ? 'opacity-100' : 'opacity-0')} />
                      <span className="truncate">{tm.name}{tm.role ? <span className="text-muted-foreground text-xs ml-1">({tm.role})</span> : null}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {visibleCustomers.length > 0 && (
              <CommandGroup heading="Customers">
                {visibleCustomers.map(c => {
                  const searchText = `customer ${c.name} ${c.phone || ''} ${c.email || ''}`.toLowerCase();
                  return (
                    <CommandItem key={c.id} value={searchText} onSelect={() => selectAndClose({ referrer_type: 'customer', referrer_id: c.id })}>
                      <Check className={cn('w-4 h-4 mr-2', type === 'customer' && id === c.id ? 'opacity-100' : 'opacity-0')} />
                      <span className="truncate">{c.name}{c.phone ? <span className="text-muted-foreground text-xs ml-1">({c.phone})</span> : null}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ReferrerSelect;
